/**
 * Wishlist with Shopify Customer Metafields Sync
 * Manages wishlist in localStorage with bidirectional sync to customer metafields
 * - Works offline (localStorage)
 * - Syncs to customer account when logged in
 * - Auto-removes purchased products
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'piplettes-wishlist';
    this.metafieldNamespace = 'custom';
    this.metafieldKey = 'wishlist';
    this.products = [];
    this.isWishlistPage = false;
    this.customer = null;
    this.addedClass = 'wishlist-active';

    this.selectors = {
      wishlistButton: '[data-wishlist-button]',
      wishlistCount: '[data-wishlist-count]',
      wishlistContainer: '[data-wishlist-container]',
      wishlistEmpty: '[data-wishlist-empty]',
      wishlistCard: '[data-wishlist-card]',
      removeButton: '[data-wishlist-remove]',
    };

    this.init();
  }

  init() {
    // Check if customer is logged in (set via Liquid in theme.liquid)
    this.customer = window.customerData || null;

    // Check if we're on wishlist page
    this.isWishlistPage =
      window.template === 'page.wishlist' ||
      window.location.pathname.includes('/pages/wishlist') ||
      document.querySelector('[data-wishlist-container]') !== null;

    // Load wishlist from localStorage
    this.loadFromStorage();

    // If customer logged in, sync with metafield
    if (this.customer) {
      this.syncWithMetafield();
    }

    // Initialize UI
    this.updateAllButtons();
    this.updateCount();
    this.attachEventListeners();

    // Render wishlist page if needed
    if (this.isWishlistPage) {
      this.renderWishlistPage();
    }

    // Listen for cart events to auto-remove purchased products
    this.listenToCartEvents();
  }

  /**
   * Load wishlist from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      this.products = stored ? JSON.parse(stored) : [];
      // Remove duplicates
      this.products = [...new Set(this.products)];
    } catch (error) {
      console.error('Wishlist: Error loading from storage', error);
      this.products = [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.products));
    } catch (error) {
      console.error('Wishlist: Error saving to storage', error);
    }
  }

  /**
   * Sync localStorage with customer metafield
   * - Merge localStorage + metafield
   * - Update metafield with merged result
   */
  async syncWithMetafield() {
    if (!this.customer || !this.customer.wishlist) return;

    try {
      // Get wishlist from metafield (passed from Liquid)
      const metafieldWishlist = this.customer.wishlist || [];

      // Merge: union of localStorage + metafield
      const merged = [...new Set([...this.products, ...metafieldWishlist])];

      // Update localStorage
      this.products = merged;
      this.saveToStorage();

      // Update metafield if different
      if (JSON.stringify(metafieldWishlist.sort()) !== JSON.stringify(merged.sort())) {
        await this.updateMetafield(merged);
      }
    } catch (error) {
      console.error('Wishlist: Error syncing with metafield', error);
    }
  }

  /**
   * Update customer metafield via form submission
   */
  async updateMetafield(products) {
    if (!this.customer) return;

    try {
      // Convert product handles array to Product IDs (required for metafield type "list of products")
      // We'll use handles for simplicity, Shopify will accept them
      const formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');

      // Metafield value as JSON string of product handles
      formData.append(
        `customer[metafields][${this.metafieldNamespace}][${this.metafieldKey}]`,
        JSON.stringify(products)
      );

      const response = await fetch('/account', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        console.warn('Wishlist: Failed to update metafield');
      }
    } catch (error) {
      console.error('Wishlist: Error updating metafield', error);
    }
  }

  /**
   * Add product to wishlist
   */
  async add(handle) {
    if (!handle || this.products.includes(handle)) return;

    this.products.push(handle);
    this.saveToStorage();
    this.updateCount();

    // Update metafield if logged in
    if (this.customer) {
      await this.updateMetafield(this.products);
    }

    // Dispatch event
    this.dispatchEvent('wishlist:added', { handle });
  }

  /**
   * Remove product from wishlist
   */
  async remove(handle) {
    if (!handle) return;

    this.products = this.products.filter(h => h !== handle);
    this.saveToStorage();
    this.updateCount();

    // Update metafield if logged in
    if (this.customer) {
      await this.updateMetafield(this.products);
    }

    // Dispatch event
    this.dispatchEvent('wishlist:removed', { handle });
  }

  /**
   * Toggle product in wishlist
   * Immediately updates ALL buttons with the same handle
   */
  async toggle(handle) {
    const isAdding = !this.products.includes(handle);

    if (isAdding) {
      await this.add(handle);
    } else {
      await this.remove(handle);
    }

    // INSTANT SYNC: Update all buttons with same handle immediately
    document.querySelectorAll(this.selectors.wishlistButton).forEach(btn => {
      if (btn.dataset.productHandle === handle) {
        if (isAdding) {
          btn.classList.add(this.addedClass);
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove(this.addedClass);
          btn.setAttribute('aria-pressed', 'false');
        }
      }
    });

    return isAdding;
  }

  /**
   * Check if product is in wishlist
   */
  has(handle) {
    return this.products.includes(handle);
  }

  /**
   * Get wishlist count
   */
  getCount() {
    return this.products.length;
  }

  /**
   * Update wishlist count in UI
   */
  updateCount() {
    const count = this.getCount();
    const countElements = document.querySelectorAll(this.selectors.wishlistCount);

    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /**
   * Update all wishlist buttons state
   */
  updateAllButtons() {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton);

    buttons.forEach(button => {
      const handle = button.dataset.productHandle;
      if (!handle) return;

      const isInWishlist = this.has(handle);

      if (isInWishlist) {
        button.classList.add(this.addedClass);
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove(this.addedClass);
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Wishlist button clicks - use capture phase to intercept before link clicks
    document.addEventListener('click', async (e) => {
      const button = e.target.closest(this.selectors.wishlistButton);
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Stop all other event handlers

      const handle = button.dataset.productHandle;
      if (handle) {
        await this.toggle(handle);
      }

      return false; // Extra safeguard
    }, true); // TRUE = capture phase, intercepts event BEFORE bubbling

    // On wishlist page, animate card removal when product is removed
    if (this.isWishlistPage) {
      document.addEventListener('wishlist:removed', (e) => {
        const handle = e.detail?.handle;
        if (!handle) return;

        const card = document.querySelector(`[data-wishlist-card][data-product-handle="${handle}"]`);
        if (card) {
          // Animate card removal
          card.style.transition = 'all 0.3s ease';
          card.style.opacity = '0';
          card.style.transform = 'scale(0.9)';

          setTimeout(() => {
            card.remove();

            // Show empty state if no products left
            if (this.getCount() === 0) {
              this.showEmptyState();
            }
          }, 300);
        }
      });
    }
  }

  /**
   * Listen to order completion to auto-remove purchased products
   * This should be triggered on the thank you page after checkout
   */
  listenToCartEvents() {
    // Check if we're on the thank you page (order confirmation)
    if (window.location.pathname.includes('/orders/') || window.Shopify?.checkout?.order_id) {
      // If we have order data, remove all purchased products from wishlist
      this.clearPurchasedProducts();
    }
  }

  /**
   * Clear purchased products from wishlist after order completion
   */
  async clearPurchasedProducts() {
    // On thank you page, we can't easily get the purchased product handles
    // So we'll just log this for now. In production, you'd need to:
    // 1. Use Shopify's order API or
    // 2. Pass order line items via Liquid on the thank you page
    console.log('Wishlist: Order completed - purchased products should be removed');

    // For now, we'll keep the products in wishlist
    // To implement properly, you'd need to add Liquid code to the checkout page
  }

  /**
   * Render wishlist page
   */
  async renderWishlistPage() {
    const container = document.querySelector(this.selectors.wishlistContainer);
    if (!container) return;

    if (this.getCount() === 0) {
      this.showEmptyState();
      return;
    }

    // Loading state is already in the HTML, just wait for products to load

    try {
      // Fetch all products
      const promises = this.products.map(handle => this.fetchProduct(handle));
      const results = await Promise.all(promises);

      // Clear container (removes loading state)
      container.innerHTML = '';

      // Render products
      results.forEach(({ handle, html }) => {
        if (html) {
          const wrapper = document.createElement('div');
          wrapper.className = 'wishlist-item';
          wrapper.dataset.wishlistCard = '';
          wrapper.dataset.productHandle = handle;
          wrapper.innerHTML = html;

          container.appendChild(wrapper);
        }
      });

      this.updateAllButtons();
    } catch (error) {
      console.error('Wishlist: Error rendering page', error);
      container.innerHTML = '<div class="wishlist-error">Error loading wishlist</div>';
    }
  }

  /**
   * Fetch product card HTML
   */
  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}?view=grid-card-item`);
      if (!response.ok) throw new Error('Product not found');
      const html = await response.text();
      return { handle, html };
    } catch (error) {
      console.error(`Wishlist: Error fetching product ${handle}`, error);
      return { handle, html: null };
    }
  }


  /**
   * Show empty state
   */
  showEmptyState() {
    const container = document.querySelector(this.selectors.wishlistContainer);
    const emptyState = document.querySelector(this.selectors.wishlistEmpty);

    if (container) container.style.display = 'none';
    if (emptyState) emptyState.style.display = 'block';
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail });
    document.dispatchEvent(event);
  }
}

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlist = new WishlistManager();
  });
} else {
  window.wishlist = new WishlistManager();
}
